/**
 * PlanningTable — Smartsheet-style container that groups schedules by week
 * and renders both TaskRows (saved) and DraftRows (unsaved) with a sticky
 * "N drafts · Save all" header when drafts exist.
 */
import React, { useMemo } from "react";
import { Loader2, ListPlus, Save, Calendar, Plus, GripHorizontal } from "lucide-react";
import TaskRow from "./TaskRow";
import DraftRow from "./DraftRow";
import { TASK_COLUMN_DEFS, DEFAULT_COLUMN_ORDER } from "./columns";

/** Convert `2026-W30` → "21 Jul – 27 Jul 2026" for the group header. */
function weekRangeLabel(isoWeek) {
  if (!isoWeek) return "";
  const m = /^(\d{4})-W(\d{2})$/.exec(isoWeek);
  if (!m) return isoWeek;
  const year = parseInt(m[1], 10);
  const week = parseInt(m[2], 10);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1 + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const opts = { day: "2-digit", month: "short" };
  return `${monday.toLocaleDateString("en-GB", opts)} – ${sunday.toLocaleDateString("en-GB", opts)} ${year}`;
}

export default function PlanningTable({
  rows,
  drafts = [],
  onDraftChange, onDraftSave, onDraftDiscard, defaultWeek,
  onSaveAll, autoSave, onToggleAutoSave, bulkSaving, validDraftCount,
  groupByWeek = true,
  onEditProject, onCarryOver, onDeleteSchedule, onQuickSaveSchedule, onSaveProgress, onSaveStatus,
  onDropDraftOnWeek, onMoveSchedule, onInsertRowForWeek, onSaveField, onReorderInWeek, onReorderDropInWeek,
  selectedRowId, onSelectRow,
  currentWeek,
  currentWeekFrameColor = "#10b981",
  columnOrder = DEFAULT_COLUMN_ORDER,
  onReorderColumns,
  hiddenColumns = [],
  statusColors = {},
  emptyMsg,
}) {
  const hiddenSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);
  const visibleOrder = useMemo(
    () => columnOrder.filter((id) => !hiddenSet.has(id)),
    [columnOrder, hiddenSet]
  );
  const grouped = useMemo(() => {
    if (!groupByWeek) return [{ week: null, rows, drafts: [] }];
    // Group both saved rows AND drafts under their respective weeks.
    // Order: future weeks TOP, current week MIDDLE (highlighted), past weeks BOTTOM.
    const map = {};
    rows.forEach((r) => {
      map[r.week] = map[r.week] || { rows: [], drafts: [] };
      map[r.week].rows.push(r);
    });
    // Sort rows within each week by sort_order (real rows first, shadows last)
    Object.values(map).forEach((g) => {
      g.rows.sort((a, b) => {
        if (a.is_shadow !== b.is_shadow) return a.is_shadow ? 1 : -1;
        return (a.sort_order ?? 0) - (b.sort_order ?? 0);
      });
    });
    (drafts || []).forEach((d) => {
      if (!d.week) return;
      map[d.week] = map[d.week] || { rows: [], drafts: [] };
      map[d.week].drafts.push(d);
    });
    // Sort DESCENDING (future first) so W35 → W30 (current, middle) → W25 (bottom)
    return Object.entries(map)
      .sort(([a], [b]) => (a < b ? 1 : -1))
      .map(([week, { rows: rs, drafts: ds }]) => ({ week, rows: rs, drafts: ds }));
  }, [rows, drafts, groupByWeek]);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0f1a2e] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-white/5 border-b border-white/10">
              {visibleOrder.map((id) => {
                const col = TASK_COLUMN_DEFS[id];
                if (!col) return null;
                const movable = !col.frozen && !!onReorderColumns;
                return (
                  <th key={id}
                      className={`${col.thClass} ${movable ? "cursor-move select-none" : ""} group`}
                      title={col.title}
                      draggable={movable}
                      data-col-id={id}
                      data-testid={`col-header-${id}`}
                      onDragStart={movable ? (e) => {
                        e.dataTransfer.setData("application/x-capacity-column", id);
                        e.dataTransfer.effectAllowed = "move";
                      } : undefined}
                      onDragOver={movable ? (e) => {
                        const src = e.dataTransfer.types.includes("application/x-capacity-column");
                        if (!src) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        e.currentTarget.classList.add("bg-cyan-500/20");
                      } : undefined}
                      onDragLeave={movable ? (e) => {
                        e.currentTarget.classList.remove("bg-cyan-500/20");
                      } : undefined}
                      onDrop={movable ? (e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove("bg-cyan-500/20");
                        const dragged = e.dataTransfer.getData("application/x-capacity-column");
                        if (!dragged || dragged === id) return;
                        const newOrder = columnOrder.filter((x) => x !== dragged);
                        const target = newOrder.indexOf(id);
                        if (target < 0) return;
                        newOrder.splice(target, 0, dragged);
                        onReorderColumns?.(newOrder);
                      } : undefined}>
                    <span className="inline-flex items-center gap-1">
                      {movable && <GripHorizontal className="w-2.5 h-2.5 opacity-30 group-hover:opacity-70"/>}
                      {col.label}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {drafts.length > 0 && (
              <tr>
                <td colSpan={visibleOrder.length} className="px-3 py-1.5 bg-cyan-500/5 border-b border-cyan-400/20">
                  <div className="flex items-center gap-2 flex-wrap text-[10px]">
                    <span className="uppercase tracking-widest text-cyan-300 font-black flex items-center gap-1.5">
                      <ListPlus className="w-3 h-3"/> {drafts.length} draft{drafts.length !== 1 ? "s" : ""}
                      {validDraftCount > 0 && <span className="text-emerald-300"> · {validDraftCount} ready</span>}
                    </span>
                    <span className="text-cyan-200/70">Drafts appear inside the week they belong to · drag to move</span>
                    <div className="ml-auto flex items-center gap-2">
                      {onToggleAutoSave && (
                        <label className="inline-flex items-center gap-1.5 uppercase tracking-widest font-black text-slate-300 cursor-pointer">
                          <input type="checkbox" checked={autoSave}
                                 onChange={(e) => onToggleAutoSave(e.target.checked)}
                                 data-testid="toggle-autosave"/>
                          Auto-save
                        </label>
                      )}
                      {onSaveAll && (
                        <button onClick={onSaveAll} disabled={bulkSaving || validDraftCount === 0}
                                data-testid="btn-save-all"
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-900 uppercase tracking-widest font-black">
                          {bulkSaving ? <Loader2 className="w-3 h-3 animate-spin"/> : <Save className="w-3 h-3"/>}
                          Save all ({validDraftCount})
                        </button>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {rows.length === 0 && drafts.length === 0 && (
              <tr>
                <td colSpan={visibleOrder.length} className="px-4 py-12 text-center text-sm text-slate-500 italic">
                  {emptyMsg || "No projects in the visible weeks. Use + Insert row or import a Smartsheet CSV."}
                </td>
              </tr>
            )}
            {grouped.map(({ week, rows: rs, drafts: ds = [] }) => {
              const isCurrent = week === currentWeek || (rs[0] && rs[0].is_current);
              const frameCol = isCurrent ? currentWeekFrameColor : null;
              // Inline styles applied to every row's first + last <td> to create
              // the L and R sides of the "frame" around the current-week block.
              const framePad = frameCol
                ? { boxShadow: `inset 4px 0 0 0 ${frameCol}, inset -4px 0 0 0 ${frameCol}` }
                : undefined;
              return (
              <React.Fragment key={week || "flat"}>
                {/* Top edge bar of the current-week frame */}
                {isCurrent && groupByWeek && week && (
                  <tr aria-hidden="true">
                    <td colSpan={visibleOrder.length} className="p-0" style={{ height: 4, background: frameCol }}/>
                  </tr>
                )}
                {groupByWeek && week && (
                  <tr className="sticky-week-header">
                    <td colSpan={visibleOrder.length} className="p-0"
                        style={frameCol ? { boxShadow: `inset 4px 0 0 0 ${frameCol}, inset -4px 0 0 0 ${frameCol}` } : undefined}>
                      <div className={`flex items-center gap-3 px-4 py-1.5 border-y shadow-sm transition-colors ${
                        isCurrent
                          ? "bg-slate-600/60 border-y-slate-400/50"
                          : "bg-slate-700/70 border-slate-500/40"
                      }`}
                           style={isCurrent ? { boxShadow: `inset 0 -1px 0 0 ${frameCol}88` } : undefined}
                           data-testid={`week-group-header-${week}`}
                           data-current={isCurrent ? "true" : "false"}
                           onDragOver={(e) => {
                             // Real browsers: allow drop on any dragover — we filter in onDrop.
                             // (Type-inspection during dragover is unreliable across browsers.)
                             e.preventDefault();
                             e.dataTransfer.dropEffect = "move";
                             e.currentTarget.classList.add("ring-2", "ring-cyan-400", "ring-inset");
                           }}
                           onDragLeave={(e) => {
                             e.currentTarget.classList.remove("ring-2", "ring-cyan-400", "ring-inset");
                           }}
                           onDrop={(e) => {
                             e.preventDefault();
                             e.currentTarget.classList.remove("ring-2", "ring-cyan-400", "ring-inset");
                             // Two supported transfer types:
                             //   application/x-capacity-draft   → move an un-saved draft row's week
                             //   application/x-capacity-schedule → move a saved schedule to another week
                             const draftId = e.dataTransfer.getData("application/x-capacity-draft");
                             const schedId = e.dataTransfer.getData("application/x-capacity-schedule");
                             if (schedId && onMoveSchedule) {
                               onMoveSchedule(schedId, week);
                             } else if (draftId && onDropDraftOnWeek) {
                               onDropDraftOnWeek(draftId, week);
                             } else {
                               // Legacy fallback: any text/plain payload treated as draft id
                               const fallback = e.dataTransfer.getData("text/plain");
                               if (fallback && onDropDraftOnWeek) onDropDraftOnWeek(fallback, week);
                             }
                           }}>
                        <Calendar className={`w-4 h-4 flex-shrink-0 ${isCurrent ? "text-white" : "text-slate-200"}`}/>
                        <div className={`text-[13px] font-black tracking-wide ${isCurrent ? "text-white" : "text-slate-100"}`}>
                          Week {week.split("-W")[1]}
                        </div>
                        {isCurrent && (
                          <span className="text-[9px] uppercase tracking-widest font-black text-slate-900 bg-white px-1.5 py-0.5 rounded shadow">
                            Now
                          </span>
                        )}
                        <span className={`text-[10px] uppercase tracking-widest font-black ${isCurrent ? "text-slate-300/60" : "text-slate-400"}`}>·</span>
                        <div className={`text-[11px] font-semibold ${isCurrent ? "text-slate-200" : "text-slate-300"}`}>
                          {weekRangeLabel(week)}
                        </div>
                        <span className={`text-[10px] uppercase tracking-widest font-black ml-auto ${isCurrent ? "text-slate-200" : "text-slate-400"}`}>
                          {rs.length + ds.length} {(rs.length + ds.length) === 1 ? "row" : "rows"}{ds.length > 0 && <span className="ml-1 text-cyan-300">· {ds.length} draft{ds.length !== 1 ? "s" : ""}</span>}
                        </span>
                        {onInsertRowForWeek && (
                          <button type="button"
                                  onClick={(e) => { e.stopPropagation(); onInsertRowForWeek(week); }}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-[10px] uppercase tracking-widest font-black shadow transition"
                                  data-testid={`btn-insert-in-week-${week}`}
                                  title={`Add a draft row on ${week}`}>
                            <Plus className="w-3 h-3"/> Insert row
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                {ds.map((d) => (
                  <DraftRow key={d.id} draft={d} defaultWeek={defaultWeek}
                            frameStyle={framePad}
                            columnOrder={visibleOrder}
                            onChange={onDraftChange} onSave={onDraftSave} onDiscard={onDraftDiscard}/>
                ))}
                {rs.map((row) => (
                  <TaskRow key={row.schedule_id} row={row}
                           onEditProject={onEditProject}
                           onCarryOver={onCarryOver}
                           onDeleteSchedule={onDeleteSchedule}
                           onQuickSaveSchedule={onQuickSaveSchedule}
                           onSaveProgress={onSaveProgress}
                           onSaveStatus={onSaveStatus}
                           onSaveField={onSaveField}
                           onReorderDropInWeek={onReorderDropInWeek}
                           onMoveSchedule={onMoveSchedule}
                           isSelected={selectedRowId === row.schedule_id}
                           onSelect={onSelectRow}
                           frameStyle={framePad}
                           columnOrder={visibleOrder}
                           statusColors={statusColors}/>
                ))}
                {/* Bottom edge bar of the current-week frame */}
                {isCurrent && groupByWeek && week && (
                  <tr aria-hidden="true">
                    <td colSpan={visibleOrder.length} className="p-0" style={{ height: 4, background: frameCol }}/>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
